import React, {useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import {db} from '../lib/db';
import SafeIcon from '../common/SafeIcon';
import LinkTypeForm from '../components/LinkTypeForm';
import {LINK_TYPES, parseUrl, buildUrl} from '../lib/linkHelpers';
import * as FiIcons from 'react-icons/fi';
import {FaStar, FaTimes} from 'react-icons/fa';

const {FiEdit2, FiAlertTriangle, FiCheckSquare, FiSquare, FiEye, FiBarChart2, FiPlus, FiTrash2, FiSettings, FiX} = FiIcons;

const EditSetup = () => {
  const {state} = useLocation();
  const navigate = useNavigate();
  
  // Main Mode: 'direct' or 'smart'
  const [mode, setMode] = useState('direct');
  
  // Form Data States
  const [directType, setDirectType] = useState('url');
  const [directData, setDirectData] = useState({});

  // Smart Profile State
  const [profile, setProfile] = useState({
    company: '',
    logoUrl: '',
    bio: '',
    links: []
  });

  // Modal State for Link Editing
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLinkIndex, setEditingLinkIndex] = useState(null);
  const [editingType, setEditingType] = useState('url');
  const [editingData, setEditingData] = useState({});
  const [editingLabel, setEditingLabel] = useState('');

  // Review Filter
  const [reviewFilterEnabled, setReviewFilterEnabled] = useState(false);
  const [googleReviewLink, setGoogleReviewLink] = useState('');
  const [dummyPageMessage, setDummyPageMessage] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  useEffect(() => {
    if (!state?.tag_code) {
      navigate('/editqr');
      return;
    }

    const init = async () => {
      // Basic Settings
      setReviewFilterEnabled(state.negative_review_filter_enabled === 1);
      setGoogleReviewLink(state.google_review_link || '');
      setDummyPageMessage(state.dummy_page_message || '');
      if (state.is_active === 1) setShowAnalytics(true);

      // Mode & Data Parsing
      if (state.link_type === 'smart') {
        setMode('smart');
        setProfile(prev => ({
          ...prev,
          company: state.company_name || '',
          logoUrl: state.logo_url || '',
          bio: state.bio || ''
        }));
        const links = await db.getProfileLinks(state.tag_code);
        // Ensure at least 3 slots or use existing
        const initialLinks = links.length > 0 ? links : Array(3).fill({label: '', url: ''});
        setProfile(prev => ({...prev, links: initialLinks}));
      } else {
        setMode('direct');
        // Parse existing redirect_url
        const parsed = parseUrl(state.redirect_url || '');
        setDirectType(parsed.type);
        setDirectData(parsed.data);
      }
    };

    init();
  }, [state, navigate]);

  // --- SMART PROFILE HANDLERS ---
  const handleAddLink = () => {
    setProfile(prev => ({...prev, links: [...prev.links, {label: '', url: ''}]}));
  };

  const handleRemoveLink = (index) => {
    const newLinks = [...profile.links];
    newLinks.splice(index, 1);
    setProfile(prev => ({...prev, links: newLinks}));
  };

  const openLinkEditor = (index) => {
    const link = profile.links[index];
    const parsed = parseUrl(link.url);
    
    setEditingLinkIndex(index);
    setEditingLabel(link.label);
    setEditingType(parsed.type);
    setEditingData(parsed.data);
    setModalOpen(true);
  };

  const saveLinkEditor = () => {
    const newUrl = buildUrl(editingType, editingData);
    let newLabel = editingLabel;
    
    // Auto-label if empty
    if (!newLabel && editingType !== 'url') {
      const typeObj = LINK_TYPES.find(t => t.id === editingType);
      if (typeObj) newLabel = typeObj.label;
    }

    const newLinks = [...profile.links];
    newLinks[editingLinkIndex] = {
      ...newLinks[editingLinkIndex],
      label: newLabel,
      url: newUrl
    };
    
    setProfile(prev => ({...prev, links: newLinks}));
    setModalOpen(false);
  };

  // --- SAVE HANDLER ---
  const handleSave = async () => {
    setIsSaving(true);
    setError('');

    try {
      const updates = {is_active: 1};

      // Review Filter Logic
      updates.negative_review_filter_enabled = reviewFilterEnabled ? 1 : 0;
      if (reviewFilterEnabled) {
         // ... (Review filter logic same as before)
         updates.google_review_link = googleReviewLink;
         updates.dummy_page_message = dummyPageMessage;
      } else {
         updates.google_review_link = googleReviewLink;
         updates.dummy_page_message = dummyPageMessage;
      }

      if (mode === 'direct') {
        const finalString = buildUrl(directType, directData);
        if (!finalString) throw new Error("Please complete the required fields.");
        
        if (reviewFilterEnabled) {
          updates.redirect_url = updates.google_review_link; 
        } else {
          updates.redirect_url = finalString;
        }
        updates.link_type = 'direct';
      } else {
        // Smart Profile Validation
        if (!profile.company.trim()) throw new Error("Company Name is required.");
        
        const activeLinks = profile.links.filter(l => l.url).map(l => ({
          ...l,
          // Basic validation/cleanup if needed
        }));
        
        if (activeLinks.length === 0) throw new Error("Add at least one link.");

        updates.link_type = 'smart';
        updates.company_name = profile.company;
        updates.logo_url = profile.logoUrl;
        updates.bio = profile.bio;

        await db.syncProfileLinks(state.tag_code, activeLinks);
      }

      await db.update(state.id, updates);
      setSuccess(true);
      setShowAnalytics(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8 pb-20 relative">
        {/* Header & Analytics (Kept Same) */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-zinc-800 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiEdit2} className="text-xl text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Update Link</h1>
            <p className="text-zinc-400 text-sm">Modify where your card redirects.</p>
          </div>
        </div>
        
        {/* ... Analytics Block ... */}

        {/* Mode Toggle */}
        <div className="bg-zinc-900 p-1 rounded-xl flex border border-zinc-800">
          <button onClick={() => setMode('direct')} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${mode === 'direct' ? 'bg-zinc-800 text-white shadow' : 'text-zinc-500 hover:text-zinc-300'}`}>Direct Link</button>
          <button onClick={() => setMode('smart')} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${mode === 'smart' ? 'bg-zinc-800 text-white shadow' : 'text-zinc-500 hover:text-zinc-300'}`}>Smart Profile</button>
        </div>

        {mode === 'direct' ? (
          <div className="space-y-6">
            {/* Review Filter... */}
            <div className="bg-amber-500/10 border border-amber-500/20 p-5 rounded-2xl space-y-4">
               {/* ... (Same Review Filter UI) ... */}
               <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-amber-500 font-bold flex items-center gap-2">
                    <SafeIcon icon={FaStar} />
                    Negative Review Filter
                  </h3>
                  <p className="text-xs text-zinc-400 mt-1">
                    Capture 1-3 star ratings privately. Redirect 4-5 stars to public reviews.
                  </p>
                </div>
                <button onClick={() => setReviewFilterEnabled(!reviewFilterEnabled)} className="text-2xl text-amber-500 transition-colors">
                  <SafeIcon icon={reviewFilterEnabled ? FiCheckSquare : FiSquare} />
                </button>
              </div>
              {reviewFilterEnabled && (
                <div className="space-y-4 pt-2 animate-in fade-in">
                  <Input label="Google Review Link (Public)" placeholder="https://g.page/r/..." value={googleReviewLink} onChange={(e) => setGoogleReviewLink(e.target.value)} />
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-zinc-400 ml-1">Message for 1-3 Star Ratings</label>
                    <textarea className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-amber-500 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-4 transition-all duration-200 min-h-[100px]" placeholder="Thank you for your feedback..." value={dummyPageMessage} onChange={(e) => setDummyPageMessage(e.target.value)} />
                  </div>
                </div>
              )}
            </div>

            {!reviewFilterEnabled && (
              <div className="space-y-6 animate-in fade-in">
                {/* Type Grid */}
                <div>
                  <label className="text-sm font-medium text-zinc-400 mb-3 block ml-1">Select Link Type</label>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                    {LINK_TYPES.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setDirectType(type.id)}
                        className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${directType === type.id ? 'bg-zinc-800 border-red-500 ring-1 ring-red-500' : 'bg-zinc-900/50 border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700'}`}
                      >
                        <SafeIcon icon={type.icon} className={`text-xl mb-2 ${directType === type.id ? 'text-white' : type.color}`} />
                        <span className={`text-[10px] font-bold ${directType === type.id ? 'text-white' : 'text-zinc-500'}`}>{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Form */}
                <div className="bg-zinc-900/30 border border-zinc-800 rounded-2xl p-6">
                  <LinkTypeForm type={directType} data={directData} onChange={setDirectData} />
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Smart Profile Form */
          <div className="space-y-8 animate-in fade-in">
            <div className="space-y-4">
              <Input label="Company Name" value={profile.company} onChange={(e) => setProfile({...profile, company: e.target.value})} />
              <Input label="Logo URL (Optional)" value={profile.logoUrl || ''} onChange={(e) => setProfile({...profile, logoUrl: e.target.value})} />
              <Input label="Bio" value={profile.bio} onChange={(e) => setProfile({...profile, bio: e.target.value})} />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="text-sm font-medium text-zinc-400">Profile Links</label>
                <button onClick={handleAddLink} className="text-xs font-bold text-red-500 flex items-center gap-1 hover:text-red-400">
                  <SafeIcon icon={FiPlus} /> Add Link
                </button>
              </div>
              
              <div className="space-y-3">
                {profile.links.map((link, i) => (
                  <div key={i} className="flex gap-2 items-center bg-zinc-900/30 p-2 rounded-xl border border-zinc-800 group">
                    <span className="text-zinc-600 text-xs w-6 text-center font-bold">{i + 1}</span>
                    
                    <div className="flex-1 grid gap-2">
                       <div className="flex gap-2">
                         <input 
                           className="flex-1 bg-transparent text-sm text-white px-2 py-1 outline-none border-b border-transparent focus:border-red-500 placeholder-zinc-600" 
                           placeholder="Link Label" 
                           value={link.label} 
                           onChange={(e) => {
                             const newLinks = [...profile.links];
                             newLinks[i].label = e.target.value;
                             setProfile(prev => ({...prev, links: newLinks}));
                           }} 
                         />
                         <button 
                           onClick={() => openLinkEditor(i)}
                           className="px-3 py-1 text-xs font-bold bg-zinc-800 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-700 flex items-center gap-1 transition-colors"
                         >
                           <SafeIcon icon={FiSettings} /> Edit
                         </button>
                       </div>
                       <input 
                         className="w-full bg-transparent text-xs text-zinc-500 px-2 py-0.5 outline-none font-mono truncate" 
                         placeholder="URL will appear here..." 
                         value={link.url} 
                         readOnly
                       />
                    </div>

                    <button 
                      onClick={() => handleRemoveLink(i)}
                      className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                ))}
                
                {profile.links.length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed border-zinc-800 rounded-xl">
                    <p className="text-zinc-500 text-sm mb-2">No links added yet.</p>
                    <Button variant="secondary" onClick={handleAddLink} className="w-auto mx-auto text-xs py-2">Add your first link</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* --- MODAL FOR LINK EDITING --- */}
        {modalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
            <div className="bg-zinc-950 border border-zinc-800 w-full max-w-lg rounded-2xl p-6 relative z-10 animate-in zoom-in-95 duration-200 shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold">Edit Link Content</h3>
                <button onClick={() => setModalOpen(false)} className="text-zinc-500 hover:text-white">
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>

              <div className="mb-6">
                <Input label="Button Label" placeholder="e.g. Contact Me" value={editingLabel} onChange={e => setEditingLabel(e.target.value)} />
              </div>

              <div className="mb-6">
                <label className="text-sm font-medium text-zinc-400 mb-3 block">Link Type</label>
                <div className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                  {LINK_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setEditingType(type.id)}
                      className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all ${editingType === type.id ? 'bg-zinc-800 border-red-500 ring-1 ring-red-500' : 'bg-zinc-900/50 border-zinc-800 hover:bg-zinc-800'}`}
                    >
                      <SafeIcon icon={type.icon} className={`text-lg mb-1 ${editingType === type.id ? 'text-white' : type.color}`} />
                      <span className={`text-[9px] font-bold truncate w-full text-center ${editingType === type.id ? 'text-white' : 'text-zinc-500'}`}>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6 max-h-[40vh] overflow-y-auto">
                <LinkTypeForm type={editingType} data={editingData} onChange={setEditingData} />
              </div>

              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                <Button onClick={saveLinkEditor}>Save Link</Button>
              </div>
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div className="pt-6 border-t border-zinc-800 mt-8">
           {error && (
            <div className="text-center p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-xs flex items-center justify-center gap-2 mb-4">
              <SafeIcon icon={FiAlertTriangle} />
              {error}
            </div>
          )}
          <Button onClick={handleSave} className={success ? "bg-green-600" : ""} disabled={isSaving}>
            {success ? "Saved Successfully!" : (isSaving ? "Saving Changes..." : "Save Changes")}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default EditSetup;